/**
 * PrescribedSupplies.tsx — THE ONE RENDER OF A PRESCRIPTION'S SUPPLIES.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescription supplies
 * (2026-09-24).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY SUPPLIES HAVE THEIR OWN COMPONENT, AND NEVER GO THROUGH `PrescribedItems`.
 *
 * A live patient received her pens with nothing to use them with. The clinician
 * had written the requirement into the free-text prescription notes — which never
 * reach the pharmacy's dispensing email — so the fix was to make supplies a
 * STRUCTURED part of the prescription (CTO + clinical lead, 2026-09-24).
 *
 * They are deliberately NOT medication lines. `ui/PrescribedItems` and
 * `lib/prescribedLine` exist to render a PRESCRIBED MEDICATION truthfully — a
 * strength, a formulation, directions, each flagged "Not recorded" when absent.
 * A supply has none of those, so routing one through that renderer would print a
 * row of amber "Not recorded" pills under something that is complete, and teach
 * a reader to ignore the flag that matters most on a clinical record.
 *
 * So there are two renderers and they never cross: medication through
 * `PrescribedItems`, supplies through this file. Every surface that shows a
 * prescription's supplies — the clinician's history, the admin subscription
 * drill-down, the pharmacy console's alert and the prescribe confirmation — gets
 * its wording from HERE, so no two of them can describe the same supply two ways.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ ABSENT IS NOT NONE (the estate's rule, applied here). An orders build that
 * predates supplies sends no `supplies` key; that renders NOTHING, because this
 * screen cannot tell what the prescription carried. An explicit `[]` is a fact
 * about the stored record and is said in words.
 *
 * R28: no supply name appears in this file or its spec. Names arrive from the
 * server; the spec uses neutral names.
 */
import type { CSSProperties } from 'react';
import type { PrescribedSupply } from '../api/types';

/** What one rendered supply line needs: a quantity and the SERVER's name. */
export interface SupplyLine {
  quantity: number;
  name: string;
}

/**
 * ONE supply as one line of text: `"<quantity> × <name>"` — the same shape
 * orders-service prints on the signed PDF and in the dispensing email, so the
 * pharmacist reads the same words on paper, in the inbox and on the console.
 *
 * The NAME is the server's, verbatim. The browser composes only the quantity
 * the clinician typed (or the server stored) beside it, and never words a unit
 * of its own — the unit is on the wire and is not needed to read the line.
 */
export function supplyLineText(supply: SupplyLine): string {
  return `${supply.quantity} × ${supply.name}`;
}

const LABEL_STYLE: CSSProperties = {
  color: 'var(--gps-muted)',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.02em',
  margin: 0,
};

/**
 * The bare list — one `<li>` per supply, in the order the server stored them.
 *
 * `label` is REQUIRED: the list is the thing a screen-reader user navigates, and
 * two of these can sit on one screen (a history with several prescriptions), so
 * each surface names what its list holds.
 */
export function SupplyLines({ supplies, label }: { supplies: SupplyLine[]; label: string }) {
  return (
    <ul aria-label={label} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '2px' }}>
      {supplies.map((supply, index) => (
        // No id on the wire; the list is never reordered or filtered, so the
        // position is the stable key.
        <li key={`${supply.name}-${index}`} style={{ fontWeight: 600, color: 'var(--gps-text)', overflowWrap: 'anywhere' }}>
          {supplyLineText(supply)}
        </li>
      ))}
    </ul>
  );
}

/**
 * The supplies block under a prescription's items, on the clinician and admin
 * history surfaces.
 */
export function PrescribedSupplies({ supplies }: { supplies: PrescribedSupply[] | null | undefined }) {
  // An older orders build (or a null the contract does not send): we cannot tell,
  // so we say nothing rather than claim the prescription carried none.
  if (!Array.isArray(supplies)) return null;
  return (
    <div
      style={{
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
        background: 'var(--gps-surface-2)',
        padding: '10px 12px',
        marginTop: '8px',
        fontSize: '13.5px',
      }}
    >
      <p style={{ ...LABEL_STYLE, marginBottom: '4px' }}>Supplies</p>
      {supplies.length === 0 ? (
        // A statement about the DOCUMENT, not the parcel: before 2026-09-24 a
        // supply could only be asked for in the notes, so an older prescription
        // listing none says nothing about what the pharmacy actually sent.
        <p style={{ margin: 0, color: 'var(--gps-muted)' }}>No supplies listed on this prescription.</p>
      ) : (
        <SupplyLines supplies={supplies} label="Supplies on this prescription" />
      )}
    </div>
  );
}
